import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("karinox_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch {
      localStorage.removeItem("karinox_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Auto-login as admin ONLY in preview/test environment (env vars must be set)
    const isPreview = window.location.hostname.includes("preview.emergentagent.com");
    const previewEmail = process.env.REACT_APP_PREVIEW_EMAIL;
    const previewPassword = process.env.REACT_APP_PREVIEW_PASSWORD;
    const autoLogin = async () => {
      try {
        const { data } = await api.post("/auth/login", { email: previewEmail, password: previewPassword });
        localStorage.setItem("karinox_token", data.token);
        setUser(data.user);
        setLoading(false);
      } catch {
        setLoading(false);
      }
    };
    const token = localStorage.getItem("karinox_token");
    if (!token && isPreview && previewEmail && previewPassword) {
      autoLogin();
    } else if (token) {
      checkAuth();
    } else {
      setLoading(false);
    }
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("karinox_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const googleLogin = async (googleData) => {
    const { data } = await api.post("/auth/google", googleData);
    localStorage.setItem("karinox_token", data.token);
    setUser(data.user);
    return { user: data.user, needs_phone: data.needs_phone };
  };

  const updateUser = (newUser) => setUser(newUser);

  const logout = () => {
    localStorage.removeItem("karinox_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, googleLogin, logout, checkAuth, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
